import { i18n } from "../i18n/i18n";

import React, { Component } from 'react'
import { RouteComponentProps, withRouter } from 'react-router-dom'
import { startCase } from 'lodash'
import routes from '../routes/routes'
import timezoneApi, { TimezoneData } from '../apis/timezone.api'
import subscriptionApi, { SubscriptionCreateRequest, SubscriptionType, SubscriptionPackage } from '../apis/subscriptionApi'
import {
  ReadCallbackData as GooglePlaceReadCallbackData
} from '../apis/googlePlace.api'
import alertService from '../services/alert.service'
import timezoneService from '../services/timezone.service'
import trialService from '../services/trial.service'
import Card from '../components/Card'
import Heading from '../components/Heading'
import Logo from '../components/Logo'
import Row from '../components/Row/Row'
import InputText from '../components/InputText'
import Select from '../components/Select'
import CardButton from '../components/CardButton'
import AddressSearchPicker from '../components/AddressSearchPicker'
import SubscriptionSelector from '../components/SubscriptionSelector/SubscriptionSelector'

interface Props extends RouteComponentProps {
  type: SubscriptionType
  onSubscribe: (
    fullName: string,
    role: string,
    username: string,
    email: string,
    phone: string,
    organizationName: string,
    timezone: string,
    address: string,
    city: string,
    state: string,
    zip: string,
    amountSelected: number,
    selectedSubscriptionId : number
  ) => void
}

interface State {
  fullName: string
  role: string
  username: string
  email: string
  phone: string
  organizationName: string
  timezone: string
  address: string
  city: string
  state: string
  zip: string
  timezones: TimezoneData[],
  subscriptionPackages: SubscriptionPackage[]
  selectedSubscriptionId : number
  amountSelected : number
}

class SubscribeForm extends Component<Props, State> {
  initialState: State = {
    fullName: '',
    role: '',
    username: '',
    email: '',
    phone: '',
    organizationName: '',
    timezone: '',
    address: '',
    city: '',
    state: '',
    zip: '',
    timezones: [],
    subscriptionPackages: [],
    selectedSubscriptionId : 0,
    amountSelected : 0
  }

  constructor (props: Props) {
    super(props)
    this.state = this.initialState
    this.handleInputChange = this.handleInputChange.bind(this)
    this.handleSubscribeClick = this.handleSubscribeClick.bind(this)
  }

  get roleSelectOptions () {
    return trialService.roleSelectOptions()
  }

  get timezoneSelectOptions () {
    return timezoneService.selectOptions(this.state.timezones)
  }

  handleInputChange (name: string, value: string) {
    if (name === 'fullName') this.setState({ fullName: value })
    if (name === 'role') this.setState({ role: value })
    if (name === 'username') this.setState({ username: value })
    if (name === 'email') this.setState({ email: value })
    if (name === 'phone') this.setState({ phone: value })
    if (name === 'organizationName') this.setState({ organizationName: value })
    else if (name === 'timezone') this.setState({ timezone: value })
  }

  handleSubscribeClick () {
    const { type } = this.props
    const { fullName, role, username, email, phone } = this.state
    const { organizationName, timezone, address, city, state, zip } = this.state
    const { selectedSubscriptionId, amountSelected } = this.state
    const payload: SubscriptionCreateRequest = {}
    payload.type = type
    if (fullName) payload.fullName = fullName
    if (role) payload.role = role
    if (username) payload.username = username
    if (email) payload.email = email
    if (phone) payload.phone = phone
    if (organizationName) payload.organizationName = organizationName
    if (timezone) payload.timezone = timezone
    if (address) payload.address = address
    if (city) payload.city = city
    if (state) payload.state = state
    if (zip) payload.zip = zip
    
    this.props.onSubscribe(fullName, role, username, email, phone, 
      organizationName, timezone, address, city, state, zip,
      amountSelected, selectedSubscriptionId)

  }

  handleAddressSelect = (addressData: GooglePlaceReadCallbackData) => {
    if (!addressData) return
    const { address, city, state, zip } = addressData
    this.setState({ address, city, state, zip })
  }

  listTimezones () {
    timezoneApi.list(timezones => {
      if (!timezones) return
      this.setState({ timezones })
    })
  }

  componentDidMount () {
    this.listTimezones()

    subscriptionApi.list(subscriptions => {
      if (!subscriptions) return
      this.setState({subscriptionPackages : subscriptions})
    })

  }

  handleSelectSubscription = (subscriptionId: number, amount: number) => {
    console.log('Selected subscription ID:', subscriptionId);
    this.setState({selectedSubscriptionId: subscriptionId, amountSelected: amount})
  };

  render () {
    return (
      <Card className='mt-12'>
        <div className='flex justify-between mb-5'>
          <div>
            <Heading
              text='Welcome to QPRO+'
              className='text-theme' />
            <div className="text-secondary">
              Fill out the form to request for a 15 days free trial
              <br />
            </div>
          </div>
          <Logo type='icon' />
        </div>
        <Row column={2}>
          <InputText
            name="fullName"
            value={this.state.fullName}
            label={i18n("Full name")}
            placeholder="John Smith"
            onChange={this.handleInputChange} />
          <Select
            name="role"
            value={this.state.role}
            options={this.roleSelectOptions}
            label={i18n("Role")}
            onChange={this.handleInputChange} />
          <InputText
            name="username"
            value={this.state.username}
            label={i18n("Username")}
            onChange={this.handleInputChange} />
          <InputText
            name="email"
            value={this.state.email}
            label={i18n("Email")}
            onChange={this.handleInputChange} />
          <InputText
            name="phone"
            value={this.state.phone}
            label={i18n("Phone")}
            onChange={this.handleInputChange} />
        </Row>
        <Heading size='small' text={i18n("Organization")} className='mb-2' />
        <Row column={2}>
          <InputText
            name="organizationName"
            value={this.state.organizationName}
            label={i18n("Organization name")}
            placeholder="Your company name or similar"
            onChange={this.handleInputChange} />
          <Select
            name="timezone"
            value={this.state.timezone}
            options={this.timezoneSelectOptions}
            label={i18n("Timezone")}
            onChange={this.handleInputChange} />
          <AddressSearchPicker onSelect={this.handleAddressSelect} />
        </Row>
        <div className="text-secondary mb-3">
          {i18n("byClicking")}
          <a
            href="https://www.quandosolutions.com/terms-of-service"
            className="text-blue-500"
            target='_blank'
            rel="noreferrer">{i18n("Terms of service")}</a> {i18n("and our")} &nbsp;
          <a
            href="https://www.quandosolutions.com/privacy-policy"
            className="text-blue-500"
            target='_blank'
            rel="noreferrer">{i18n("Privacy Policy")}</a>
          .
        </div>
        <SubscriptionSelector
          subscriptions={this.state.subscriptionPackages} 
          onSelectSubscription={this.handleSelectSubscription} 
          selectedSubscriptionId={this.state.selectedSubscriptionId}
        /><br/><br/>
        <Row column={2}>
          <CardButton
            proceedText={i18n("Next")}
            onProceedClick={this.handleSubscribeClick} />
        </Row>
      </Card>
    )
  }
}

export default withRouter(SubscribeForm)
